import React, { useState, useEffect, useRef, RefObject } from "react";
import { Outlet, NavLink, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./Layout.module.css";
import appLogo from "../../assets/applogo.svg";

import { useLogin } from "../../authConfig";
import { configApi } from "../../api";

import { LoginButton } from "../../components/LoginButton";
import { IconButton } from "@fluentui/react";
import { LanguagePicker } from "../../i18n/LanguagePicker";

const Layout = () => {
    const { t, i18n } = useTranslation();
    const [menuOpen, setMenuOpen] = useState(false);
    const [showLanguagePicker, setShowLanguagePicker] = useState<boolean>(false);
    const menuRef: RefObject<HTMLDivElement> = useRef(null);

    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
            setMenuOpen(false);
        }
    };

    useEffect(() => {
        if (menuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [menuOpen]);

    useEffect(() => {
        const getConfig = async () => {
            try {
                const config = await configApi();
                setShowLanguagePicker(config.showLanguagePicker);
            } catch (error) {
                console.error("Failed to get config:", error);
            }
        };
        getConfig();
    }, []);

    const isMobileDevice = () => {
        return window.innerWidth <= 768;
    };

    return (
        <div className={styles.layout}>
            <header className={styles.header} role={"banner"}>
                <div className={styles.headerContainer} ref={menuRef}>
                    <Link to="/" className={styles.headerTitleContainer}>
                        <h3 className={styles.headerTitle}>
                            <img src={appLogo} alt="App logo" style={{ width: "50px", height: "40px", marginRight: "8px", verticalAlign: "middle" }} />
                            {t("headerTitle")}
                        </h3>
                    </Link>

                    {/* Desktop: show LoginButton and LanguagePicker inline */}
                    <div className={styles.loginMenuContainer}>
                        {!isMobileDevice() && useLogin && <LoginButton />}
                        {!isMobileDevice() && showLanguagePicker && <LanguagePicker onLanguageChange={lang => i18n.changeLanguage(lang)} />}
                        <IconButton
                            iconProps={{ iconName: "GlobalNavButton" }}
                            className={styles.menuToggle}
                            onClick={toggleMenu}
                            ariaLabel={t("labels.toggleMenu")}
                        />
                    </div>

                    {/* Mobile: show LoginButton and LanguagePicker inside menu toggle dropdown */}
                    {isMobileDevice() && menuOpen && (
                        <div className={styles.mobileMenuDropdown}>
                            <div className={styles.mobileMenuContent}>
                                <div className={styles.mobileMenuSection}>{useLogin && <LoginButton />}</div>
                                <div className={styles.mobileMenuSection}>
                                    {showLanguagePicker && <LanguagePicker onLanguageChange={lang => i18n.changeLanguage(lang)} />}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </header>

            <main className={styles.main} id="main-content">
                <Outlet />
            </main>
        </div>
    );
};

export default Layout;
